import React, { useCallback, useEffect, useState } from "react";
import {
  Dropdown,
  DropdownProps,
  Option,
  Toast,
  ToastTitle,
  useToastController,
  Field,
  Input,
  makeStyles,
} from "@fluentui/react-components";
import usePageTemplates from "../../services/templates/usePageTemplates";
import { useErrorBoundary } from "react-error-boundary";
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormGetValues } from "react-hook-form";
import { VerificationField, Template, VerificationFieldData } from "../../interfaces";

interface PageTemplateDropdownProps extends Partial<DropdownProps> {
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
  onVerificationDataChange?: (data: VerificationFieldData[]) => void;
  getValues: UseFormGetValues<any>;
  onFrameTemplateChange?: (frameTemplateId?: string) => void;
  onExpiryRequirementChange?: (isRequired: boolean) => void;
}

const useStyles = makeStyles({
  dynamicField: {
    marginTop: "16px",
  },
});

/**
 * The `PageTemplateDropdown` function fetches and displays a list of available templates, with
 * error handling and toast notifications.
 * @param props - The `PageTemplateDropdown` function takes a `Partial<DropdownProps>` object as its
 * `props` parameter. This object likely contains properties that can be passed to the `Dropdown`
 * component within the function. By using `Partial<DropdownProps>`, the function allows for passing
 * only a subset of the dropdown properties.
 * @returns The `PageTemplateDropdown` function is returning a JSX element that consists of a
 * `Dropdown` component. The `Dropdown` component has various props passed to it, such as
 * `aria-labelledby`, `placeholder`, `size`, and any additional props passed through `props`. The
 * `Dropdown` component is disabled based on the condition `templates === null`.
 */

const PageTemplateDropdown = (props: PageTemplateDropdownProps) => {
  const {
    register,
    setValue,
    errors,
    onVerificationDataChange,
    getValues,
    onFrameTemplateChange,
    onExpiryRequirementChange,
    ...dropdownProps
  } = props;
  const styles = useStyles();
  // Toast dispatcher
  const { dispatchToast } = useToastController();

  // Error boundary
  const { showBoundary } = useErrorBoundary();

  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [verificationFields, setVerificationFields] = useState<VerificationField[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>();

  // Collect verification data from form values
  const collectVerificationData = useCallback(
    (fields: VerificationField[]) => {
      const currentValues = getValues();
      const verificationData: VerificationFieldData[] = fields
        .filter((field) => !["Created By", "Created On", "Document Name"].includes(field.fieldName))
        .map((field) => ({
          fieldId: field.fieldId,
          fieldName: field.fieldName,
          fieldValue: currentValues[field.fieldId] || "",
        }));

      if (onVerificationDataChange) {
        onVerificationDataChange(verificationData);
      }
    },
    [getValues, onVerificationDataChange]
  );

  const applyTemplate = useCallback(
    (template: Template) => {
      setSelectedTemplateId(template.template_id);
      setValue("template_id", template.template_id);
      setVerificationFields(template.content.verificationData);
      onFrameTemplateChange?.(template.frame_template);
       onExpiryRequirementChange?.(Boolean(template.set_expiry));
      template.content.verificationData.forEach((field) => {
        setValue(field.fieldId, "");
      });
      setTimeout(() => collectVerificationData(template.content.verificationData), 0);
    },
    [collectVerificationData, onExpiryRequirementChange, onFrameTemplateChange, setValue]
  );

  useEffect(() => {
    // fetch templates list.
    const fetchTemplatesList = async () => {
      try {
        const { response } = await usePageTemplates();
        const fetchedTemplates = response?.data.results;
        setTemplates(fetchedTemplates);
        const defaultTemplate = fetchedTemplates?.find((template: Template) => template.is_default);
        if (defaultTemplate) {
          applyTemplate(defaultTemplate);
        }
      } catch (responseError) {
        if (responseError.code == "ERR_NETWORK") showBoundary(responseError);
        console.error(responseError);
        dispatchToast(
          <Toast>
            <ToastTitle>Something went wrong. Please reach out to us at support@qrmark.com</ToastTitle>
          </Toast>
        );
      }
    };

    // noinspection JSIgnoredPromiseFromCall
    fetchTemplatesList();
  }, [applyTemplate, dispatchToast, showBoundary]);

  // Handle template selection
  const handleTemplateSelection = (_event: any, data: any) => {
    if (!data.optionValue) {
      setSelectedTemplateId(undefined);
      setValue("template_id", "");
      setVerificationFields([]);
      onFrameTemplateChange?.(undefined);
      onExpiryRequirementChange?.(false);
      if (onVerificationDataChange) {
        onVerificationDataChange([]);
      }
      return;
    }

    const template = templates?.find((t) => t.template_id === data.optionValue);
    if (template) {
      applyTemplate(template);
    } else {
      setSelectedTemplateId(undefined);
      setVerificationFields([]);
      onFrameTemplateChange?.(undefined);
      onExpiryRequirementChange?.(false);
      if (onVerificationDataChange) {
        onVerificationDataChange([]);
      }
    }
  };

  return (
    <>
      <Dropdown
        aria-labelledby="page-template"
        placeholder="Select Page Template"
        size="medium"
        clearable
        value={templates?.find((template) => template.template_id === selectedTemplateId)?.name ?? ""}
        selectedOptions={selectedTemplateId ? [selectedTemplateId] : []}
        onOptionSelect={handleTemplateSelection}
        {...dropdownProps}
        {...register("template_id")}
        disabled={templates === null || templates.length === 0}
      >
        {templates &&
          templates.map((template: Template) => (
            <Option key={template.template_id} value={template.template_id}>
              {template.name}
            </Option>
          ))}
      </Dropdown>

      {/* Dynamic verification fields based on selected template */}
      {verificationFields
        .filter((field) => !["Created By", "Created On", "Document Name"].includes(field.fieldName))
        .map((field) => (
          <Field
            key={field.fieldId}
            className={styles.dynamicField}
            validationMessage={errors[field.fieldId]?.message as string}
            label={
              <>
                {field.fieldName}
                {field.isRequired && <span style={{ color: "red", marginLeft: "4px" }}>*</span>}
              </>
            }
          >
            <Input
              type="text"
              placeholder={`Enter ${field.fieldName.toLowerCase()}`}
              {...register(field.fieldId, {
                required: field.isRequired ? `${field.fieldName} is required` : false,
                onChange: () => {
                  // Update verification data when field values change
                  setTimeout(() => collectVerificationData(verificationFields), 0);
                },
              })}
            />
          </Field>
        ))}
    </>
  );
};

export default PageTemplateDropdown;
